import { AUTH_TOKEN } from "constants/AuthConstant";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setexpensetypesState } from "store/slices/expensetypes";
import { isAuth } from "./isAuth";
import { setAuthUser, signOutSuccess } from "store/slices/authSlice";
import { refreshAccessToken, isRefreshingToken } from "refreshToken";

const useExpenseTypes = () => { 
  const [Expenses, setExpenseTypes] = useState(null);
  const [Expense, setExpense] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null); 
  const base_url = process.env.REACT_APP_API_URL;
  
  const url_getAllExpenseTypes = `${base_url}/expense-types`;
  const url_addexpense = `${base_url}/expense-types`;
  const url_updateExpense = `${base_url}/expense-types`;
  
  const dispatch = useDispatch();
  const tokenFromState = useSelector((state) => state.auth.token);
  
  if (localStorage.getItem(AUTH_TOKEN) != tokenFromState) {
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(signOutSuccess());
      dispatch(setAuthUser({}));
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN);
    
    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken();
    }

    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken();
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN);
        dispatch(signOutSuccess());
        dispatch(setAuthUser({}));
        if (navigate) navigate("/login");
        throw new Error("Session expired");
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return response;
  };

  const addExpenseType = async (data, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        url_addexpense, 
        {
          method: "POST",
          body: JSON.stringify(data)
        }, 
        navigate
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }
      
      const responseData = await response.json();
      setExpense(responseData);
      await getAllExpenseTypes(navigate);
    } catch (err) {
      console.error("Failed to add expense type", err);
      setError(err.message || "Failed to add expense type");
    } finally {
      setIsLoading(false);
    }
  };

  const getAllExpenseTypes = async (navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        url_getAllExpenseTypes, 
        { method: "GET" }, 
        navigate
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }

      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};

      if (Array.isArray(data.data)) {
        setExpenseTypes(data.data);
        dispatch(setexpensetypesState(data.data));
      } else {
        setError("Les données des dépenses sont invalides.");
      }
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des dépenses :", err);
      setError(err.message || "Failed to get dépenses");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteExpenseType = async (id, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${url_addexpense}/${id}`, 
        { method: "DELETE" }, 
        navigate
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }
      
      setExpense([]);
      await getAllExpenseTypes(navigate);
    } catch (err) {
      console.error("Failed to delete expense type", err);
      setError(err.message || "Failed to delete expense type");
    } finally {
      setIsLoading(false);
    }
  };

  const updateExpenseType = async (data, id, navigate) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${url_updateExpense}/${id}`, 
        {
          method: "PUT",
          body: JSON.stringify(data)
        }, 
        navigate
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return;
      }
      
      const responseData = await response.json();
      setExpense(responseData);
      await getAllExpenseTypes(navigate);
      return responseData;
    } catch (err) {
      console.error("Update error:", err);
      setError(err.message || "expenseTypes.update.error");
    } finally {
      setIsLoading(false);
    }
  };

  const getExpenseTypeById = async (id, navigate) => {
    if (!id) return null;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `${url_getAllExpenseTypes}/${id}`, 
        { method: "GET" }, 
        navigate
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData);
        return null;
      }

      const responseData = await response.json();
      setExpense(responseData.data);
      return responseData.data;
    } catch (err) {
      console.error("Failed to get expense type", err);
      setError(err.message || "Failed to get expense type");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(signOutSuccess());
      dispatch(setAuthUser({}));
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur");
      return;
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0]);
      return;
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message);
      return;
    }

    if (errorData?.message) {
      setError(errorData.message);
      return;
    }

    setError("Erreur inconnue");
  };

  return {
    getExpenseById: getExpenseTypeById,
    deleteExpenseType, 
    updateExpense: updateExpenseType,
    addExpenseType,
    Expense,
    Expenses, 
    getAllExpenseTypes, 
    setExpenseTypes,
    setExpense, 
    isLoading, 
    error, 
    setError 
  };
};

export default useExpenseTypes;